import { isPatrolmanPlusSku, skuRequiresImeiIccid } from "../configuration-sku-family.js";
import {
  inferCarrierFromWirelessSku,
  resolveWirelessSimCarrier,
} from "../sim-iccid-carrier.js";
import { loadTlaBomIndicatesR15PcbaTray } from "./configuration-r15-pcba.js";
import type { Pool } from "pg";

/** Verizon micro SIM stocked at Configuration (station 5) via manufacturing.station_stock. */
export const CONFIGURATION_VERIZON_SIM_SKU = "30-0002";

/** Connector tray for CON-3771 header — Configuration station prestock. */
export const CONFIGURATION_CON_3771_TRAY_SKU = "CON-3771-T";

const CONFIGURATION_STATION_STOCK_SKUS_LOWER = new Set(
  [CONFIGURATION_VERIZON_SIM_SKU, CONFIGURATION_CON_3771_TRAY_SKU].map((sku) => sku.toLowerCase()),
);

export function isConfigurationStationStockSku(skuRaw: string): boolean {
  return CONFIGURATION_STATION_STOCK_SKUS_LOWER.has(skuRaw.trim().toLowerCase());
}

/** Wireless configure jobs programmed for Verizon consume one micro SIM. */
export function configurationRequiresVerizonSimCard(
  finishedSkuRaw: string,
  iccid?: string | null,
): boolean {
  const finishedSku = finishedSkuRaw.trim();
  if (!skuRequiresImeiIccid(finishedSku)) return false;
  const carrier =
    iccid != null && iccid.trim() !== ""
      ? resolveWirelessSimCarrier(iccid, finishedSku)
      : inferCarrierFromWirelessSku(finishedSku);
  return carrier === "Verizon";
}

/** FI-5A-W TLA kits that use R15-era CON-3771 header hardware need the connector tray. */
export async function configurationRequiresCon3771Tray(
  pool: Pick<Pool, "query">,
  finishedSkuRaw: string,
): Promise<boolean> {
  const finishedSku = finishedSkuRaw.trim();
  if (!isPatrolmanPlusSku(finishedSku)) return false;
  return loadTlaBomIndicatesR15PcbaTray(pool, finishedSku);
}

/** SKUs to consume at Configuration submit for this finished unit (may be empty). */
export async function resolveConfigurationStationStockSkus(
  pool: Pick<Pool, "query">,
  params: { finishedSku: string; iccid?: string | null },
): Promise<string[]> {
  const skus: string[] = [];
  if (configurationRequiresVerizonSimCard(params.finishedSku, params.iccid)) {
    skus.push(CONFIGURATION_VERIZON_SIM_SKU);
  }
  if (await configurationRequiresCon3771Tray(pool, params.finishedSku)) {
    skus.push(CONFIGURATION_CON_3771_TRAY_SKU);
  }
  return skus;
}
